import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@ooh/ui/components/skeleton';

interface TableSkeletonProps {
  rows?: number;
  columns?: number;
}

function LoadingRegion({ children }: { children: ReactNode }) {
  const { t } = useTranslation('common');
  return (
    <div role="status" aria-busy="true" aria-label={t('loading')} className="space-y-3">
      {children}
    </div>
  );
}

export function TableSkeleton({ rows = 8, columns = 4 }: TableSkeletonProps) {
  const columnIds = Array.from({ length: columns }, (_, index) => `col-${index}`);
  return (
    <LoadingRegion>
      <Skeleton className="h-9 w-full max-w-xs" />
      <Skeleton className="h-10 w-full rounded-lg" />
      {Array.from({ length: rows }, (_, row) => (
        <div key={`row-${row}`} className="flex items-center gap-4 px-3">
          {columnIds.map((id, column) => (
            <Skeleton key={id} className={column === 0 ? 'h-4 w-1/3' : 'h-4 flex-1'} />
          ))}
        </div>
      ))}
    </LoadingRegion>
  );
}

export function SectionSkeleton() {
  return (
    <LoadingRegion>
      <Skeleton className="h-4 w-2/3" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-5/6" />
    </LoadingRegion>
  );
}

export function FormSkeleton({ fields = 4 }: { fields?: number }) {
  return (
    <LoadingRegion>
      <div className="space-y-4 px-6 py-5">
        {Array.from({ length: fields }, (_, index) => (
          <div key={`field-${index}`} className="space-y-1.5">
            <Skeleton className="h-3 w-24" />
            <Skeleton className="h-9 w-full" />
          </div>
        ))}
      </div>
    </LoadingRegion>
  );
}
